import React, { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "react-responsive";
import MeasuredBy from "../../../molecules/MeasuredBy";
import {
  activeStylesContinue,
  disabledStylesContinue,
  resultScreenUI,
} from "../../../../configs";

import { CheckCircle as CheckCircleIcon } from "@mui/icons-material";

import { removeLocalStore } from "../../../../store/localStoreUtils";
import {
  capitalizeFirstLetter,
  getActualDescriptionApparel,
  getActualDescriptionShoes,
  useIsMobile,
} from "../../../../utils";
import {
  findBrandByDomain,
  getQueryParam,
} from "../../../../configs/configLoader";
import {
  postAddToCart,
  postClearMid,
  postClearUid,
  postCloseIframe,
  postSelectSize,
  postSendUid,
} from "../../../../utils/post";
import { logger } from "../../../../utils/logging";
import { useUserContext } from "../../../../store/userContext";
import { ArrowForward as ChevronRight } from "@mui/icons-material";
import { hoverStylesContinue } from "../../../../configs/stylesLoader";
import InfoStyledIcon from "../../../../assets/icons/InfoStyledIcon";
import {
  SimilarProductType,
  RecommendedSizeType,
} from "../../../../types/result";
import UnavailableGallery from "../UnavailableGallery";

import "./index.css";

type SelectorSizeType = "unfocused" | "focused" | "unavailable";

interface IPropsShoeResult {
  step: {
    number: number;
  } | null;
  restart: () => void;
  recommendation: any | null;
  selectedVariant: string;
  setSelectedVariant: React.Dispatch<React.SetStateAction<string>>;
  isSizeUnavailable: boolean;
  selectedSize: RecommendedSizeType | null;
  setSelectedSize: React.Dispatch<RecommendedSizeType | null>;
  similarProducts: SimilarProductType[] | null;
}

const ShoeResult: FC<IPropsShoeResult> = ({
  step,
  restart,
  recommendation,
  selectedVariant,
  setSelectedVariant,
  isSizeUnavailable,
  selectedSize,
  setSelectedSize,
  similarProducts,
}) => {
  logger.log("recommendation", recommendation);
  const { t } = useTranslation("components/results/result");

  const isMobile = useIsMobile();
  const isVerySmallMobile = useMediaQuery({ maxWidth: 400 });

  const userContext = useUserContext();

  const urlParameters = new URLSearchParams(window.location.search);
  const isMockData = urlParameters.get("mock") === "true";
  const isLingerie = urlParameters.get("lingerie") === "true";
  const isFootwear =
    urlParameters.get("category") === "footwear" ||
    urlParameters.get("footwear") === "true";
  const isChildren =
    urlParameters.get("category") === "children" ||
    urlParameters.get("kids") === "true";

  const fit_feedbacks = [t("shoe.size1"), t("shoe.size2"), t("shoe.size3")];

  const brandDefined = findBrandByDomain();

  const isPeserico = brandDefined?.name === "Peserico";
  const isVB = brandDefined?.name === "Victoria Beckham";
  const isIntersport = brandDefined?.name === "Intersport";
  const isGivenchy = brandDefined?.name === "Givenchy";
  const isTDF = brandDefined?.name === "TDF";

  const isGivenchyDesktop = isGivenchy && !isMobile;

  const [hovered, setHovered] = useState(false);

  const [goodSize, setGoodSize] = useState<any | null>(null);
  const [smallerSize, setSmallerSize] = useState<any | null>(null);
  const [biggerSize, setBiggerSize] = useState<any | null>(null);

  useEffect(() => {
    const sizes = ["good", "smaller", "bigger"] as const;

    sizes.forEach((size, index) => {
      let data = recommendation?.[index];

      if (isFootwear) {
        data = recommendation?.[size];
      }

      if (isChildren && !isMockData) {
        data = recommendation?.[size];
      }

      if (data) {
        const setter =
          size === "good"
            ? setGoodSize
            : size === "smaller"
              ? setSmallerSize
              : setBiggerSize;

        setter({
          label: data?.size_label || data?.label,
          reference: data?.reference,
          variant_id: data?.reference || data?.variant_id,
          possible: size === "good" ? 10 : 0,
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recommendation, isChildren, isFootwear, isMockData]);

  useEffect(() => {
    const isSelectSize = isMockData
      ? !!goodSize
      : !!goodSize && !!goodSize?.variant_id;

    if (isSelectSize) {
      setSelectedSize(goodSize);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goodSize, isMockData]);

  useEffect(() => {
    const ref = selectedSize?.reference;
    if (ref?.toString().trim()) {
      postSelectSize(ref);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSize]);

  const getSelectorSizeStyles = (type: SelectorSizeType, variant: string) => {
    const sizeStyles = resultScreenUI.sizeSelector[type]!;
    const cornerBorderWidth = "2px";
    const borderColor =
      type === "unavailable"
        ? "#CEA13F"
        : type === "focused"
          ? "#44883F"
          : "#E9E9E9";
    const borderRadius = "4px";

    const selectorSizeStyles = {
      backgroundColor: "#FFF",
      color: "#2E2E2E",
      fontWeight: 600,
      fontSize: "11px",
      textTransform:
        sizeStyles.textTransform as React.CSSProperties["textTransform"],
      borderTopLeftRadius:
        smallerSize?.variant_id && variant === "0" ? borderRadius : 0,
      borderBottomLeftRadius:
        smallerSize?.variant_id && variant === "0" ? borderRadius : 0,
      borderTopRightRadius:
        biggerSize?.variant_id && variant === "2" ? borderRadius : 0,
      borderBottomRightRadius:
        biggerSize?.variant_id && variant === "2" ? borderRadius : 0,
      borderWidthStyle: sizeStyles.borderWidth,
      borderTop: `${sizeStyles.borderWidth} solid ${borderColor}`,
      borderBottom: `${sizeStyles.borderWidth} solid ${borderColor}`,
      borderLeft: `${cornerBorderWidth} solid ${borderColor}`,
      borderRight: `${cornerBorderWidth} solid ${borderColor}`,
      letterSpacing: (sizeStyles as any)?.letterSpacing,
    };

    return selectorSizeStyles;
  };

  const firstSelectorStyles =
    smallerSize?.variant_id === selectedSize?.variant_id
      ? getSelectorSizeStyles("unavailable", "0")
      : getSelectorSizeStyles("unfocused", "0");

  const secondSelectorStyles =
    goodSize?.variant_id === selectedSize?.variant_id
      ? getSelectorSizeStyles("focused", "1")
      : getSelectorSizeStyles("unfocused", "1");

  const thirdSelectorStyles =
    biggerSize?.variant_id === selectedSize?.variant_id
      ? getSelectorSizeStyles("unavailable", "2")
      : getSelectorSizeStyles("unfocused", "2");

  const skipTextTransform = resultScreenUI.restartCTA
    .textTransform as React.CSSProperties["textTransform"];

  const sendIds = () => {
    const uid = localStorage.getItem("uid");
    if (uid && uid !== null && uid !== undefined) {
      postSendUid(uid);
    }
  };

  // const clearMid = () => {
  //   postClearMid();
  // };

  const addToCart = () => {
    logger.log(`addToCart called: ${selectedSize?.variant_id}`);
    const variantId = selectedSize?.variant_id;
    if (variantId) postAddToCart(variantId);
  };

  useEffect(() => {
    sendIds();
  }, []);

  const stripHtml = (html: string) => html.replace(/<[^>]+>/g, "");

  const continueButtonStyles = isSizeUnavailable
    ? disabledStylesContinue
    : activeStylesContinue;

  const CTAStyles: React.CSSProperties = {
    ...continueButtonStyles,
    ...(hovered ? hoverStylesContinue : {}),
    textTransform:
      continueButtonStyles.textTransform === "capitalize"
        ? "none"
        : continueButtonStyles.textTransform,
    letterSpacing: (continueButtonStyles as any)?.letterSpacing,
    padding: isTDF ? "0" : isPeserico ? "12px 16px" : isVB ? "7px" : "16px",
    height: isTDF ? "28px" : isVB ? "30px" : "auto",
    maxHeight: isTDF ? "28px" : isGivenchy ? "40px" : "fit-content",
    marginTop: isMobile ? "0" : "30px",
    position: isGivenchyDesktop ? "fixed" : "relative",
    bottom: isGivenchyDesktop ? "150px" : 0,
    width: isGivenchyDesktop ? "calc(100% - 160px)" : "",
  };

  const arrowContainerStyle: React.CSSProperties = {
    display: "inline-block",
    width: hovered && !isSizeUnavailable ? 16 : 0,
    overflow: "hidden",
    verticalAlign: "middle",
    transition: "width 160ms ease",
  };

  const iconStyle: React.CSSProperties = {
    width: 16,
    height: 16,
    opacity: hovered && !isSizeUnavailable ? 1 : 0,
    transform:
      hovered && !isSizeUnavailable ? "translateX(0)" : "translateX(-6px)",
    transition: "opacity 160ms ease, transform 160ms ease",
    display: "inline-block",
    verticalAlign: "middle",
    pointerEvents: "none",
  };

  const sizeTextColor =
    isSizeUnavailable && brandDefined?.name !== "SRP"
      ? (resultScreenUI.unavailable_size_text
          .fontColor as React.CSSProperties["color"])
      : (resultScreenUI.recommendedSize
          .fontColor as React.CSSProperties["color"]);

  const localizedSizeLabel =
    `${t("steps.result", { ns: "components/intro" })} ${selectedSize?.label}` ||
    selectedSize?.label;

  return (
    <div
      className="result"
      style={{
        marginTop: "20px",
      }}
    >
      <div className="size">
        <h1
          style={{
            color: sizeTextColor,
            fontSize: resultScreenUI.recommendedSize
              .fontSize as React.CSSProperties["fontSize"],
            fontWeight: resultScreenUI.recommendedSize
              .fontWeight as React.CSSProperties["fontWeight"],
            textAlign: resultScreenUI.recommendedSize
              .textAlign as React.CSSProperties["textAlign"],
          }}
          aria-label={localizedSizeLabel}
        >
          {selectedSize?.label || ""}
        </h1>

        {!isFootwear && !isLingerie ? (
          <>
            <div className="size-name">
              {goodSize?.variant_id === selectedSize?.variant_id && (
                <>
                  <h2
                    style={{
                      color: "#44883F",
                      fontSize: resultScreenUI.subtitles
                        .fontSize as React.CSSProperties["fontSize"],
                      fontWeight: resultScreenUI.subtitles
                        .fontWeight as React.CSSProperties["fontWeight"],
                      textAlign: resultScreenUI.subtitles
                        .textAlign as React.CSSProperties["textAlign"],
                    }}
                  >
                    {t("size.title").replace("[S]", selectedSize?.label || "")}
                  </h2>
                  <CheckCircleIcon
                    style={{
                      color: "#44883F",
                    }}
                  />
                </>
              )}
            </div>
            <div className="sizes-menu">
              <div
                className={`menu-item ${
                  smallerSize?.variant_id === selectedSize?.variant_id &&
                  "active"
                }`}
                onClick={() => {
                  if (!!smallerSize) {
                    setSelectedVariant("0");
                    setSelectedSize(smallerSize);
                  }
                }}
                style={{
                  ...firstSelectorStyles,
                  cursor: !smallerSize ? "default" : "pointer",
                  textTransform:
                    firstSelectorStyles?.textTransform === "capitalize"
                      ? "none"
                      : firstSelectorStyles?.textTransform,
                }}
              >
                <p>{!smallerSize ? "" : fit_feedbacks[0].toUpperCase()}</p>
              </div>
              <div
                className={`menu-item ${
                  goodSize?.variant_id === selectedSize?.variant_id && "active"
                }`}
                onClick={() => {
                  if (!!goodSize) {
                    setSelectedVariant("1");
                    setSelectedSize(goodSize);
                  }
                }}
                style={{
                  ...secondSelectorStyles,
                  cursor: "pointer",
                  textTransform:
                    secondSelectorStyles?.textTransform === "capitalize"
                      ? "none"
                      : secondSelectorStyles?.textTransform,
                }}
              >
                <p>{fit_feedbacks[1].toUpperCase()}</p>
              </div>
              <div
                className={`menu-item ${
                  biggerSize?.variant_id === selectedSize?.variant_id &&
                  "active"
                }`}
                onClick={() => {
                  if (!!biggerSize) {
                    setSelectedVariant("2");
                    setSelectedSize(biggerSize);
                  }
                }}
                style={{
                  ...thirdSelectorStyles,
                  cursor: !biggerSize ? "default" : "pointer",
                  textTransform:
                    thirdSelectorStyles?.textTransform === "capitalize"
                      ? "none"
                      : thirdSelectorStyles?.textTransform,
                }}
              >
                <p>{!biggerSize ? "" : fit_feedbacks[2].toUpperCase()}</p>
              </div>
            </div>
          </>
        ) : null}
      </div>

      {isLingerie ? (
        <div
          className="result-body"
          aria-label={t("size_parameter_title").replace(
            "[S]",
            selectedSize?.label || "",
          )}
          style={{
            height: "auto",
            padding: isMobile ? "10px 0" : "20px 0",
            display: "flex",
            flexDirection: "column",
            gap: "5px",
            marginBottom: "30px",
            border: "1px #E9E9E9 solid",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: "10px",
              alignItems: "center",
              padding: "0 20px",
            }}
          >
            <InfoStyledIcon
              style={{
                fontSize: "16px",
              }}
              fill={
                resultScreenUI.Description
                  .fontColor as React.CSSProperties["color"]
              }
            />
            <h3
              style={{
                margin: 0,
                fontSize: isVerySmallMobile ? "11px" : "15px",
                fontWeight: 700,
                color: resultScreenUI.Description
                  .fontColor as React.CSSProperties["color"],
                letterSpacing: (resultScreenUI.Description as any)
                  ?.letterSpacing,
              }}
            >
              {t("size_parameter_title").replace(
                "[S]",
                selectedSize?.label || "",
              )}
            </h3>
          </div>
          <p
            style={{
              margin: 0,
              paddingLeft: "45px",
              paddingRight: isMobile ? "10px" : "50px",
              fontSize: isVerySmallMobile ? "11px" : "15px",
              fontWeight: 500,
              color: resultScreenUI.Description
                .fontColor as React.CSSProperties["color"],
              letterSpacing: (resultScreenUI.Description as any)?.letterSpacing,
            }}
          >
            {t("size_parameter_description").replace(
              "[S]",
              selectedSize?.label || "",
            )}
          </p>
        </div>
      ) : (
        <div
          className="result-body-text"
          aria-label={stripHtml(
            t(
              isChildren
                ? getActualDescriptionApparel(selectedVariant, selectedSize)
                : getActualDescriptionShoes(
                    selectedSize,
                    smallerSize,
                    goodSize,
                    biggerSize,
                  ),
            ).replace("[S]", selectedSize?.label || ""),
          )}
          style={{
            borderBottom: "none",
            justifyContent: resultScreenUI.Description
              .textAlign as React.CSSProperties["justifyContent"],
            backgroundColor: "transparent",
            paddingBottom: 0,
            margin: brandDefined?.name === "SRP" ? "32px 0" : "15px 0",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "10px",
            }}
          >
            <div
              style={{
                color: "#2E2E2E",
                fontSize:
                  brandDefined?.name === "SRP"
                    ? "14px"
                    : (resultScreenUI.Description
                        .fontSize as React.CSSProperties["fontSize"]),
                fontWeight: resultScreenUI.Description
                  .fontWeight as React.CSSProperties["fontWeight"],
                textAlign: "left",
                lineHeight: "20px",
                display: "flex",
                alignItems: "center",
                minHeight: "60px",
                letterSpacing: (resultScreenUI.Description as any)
                  ?.letterSpacing,
              }}
            >
              <span
                dangerouslySetInnerHTML={{
                  __html: t(
                    isChildren
                      ? getActualDescriptionApparel(
                          selectedVariant,
                          selectedSize,
                        )
                      : getActualDescriptionShoes(
                          selectedSize,
                          smallerSize,
                          goodSize,
                          biggerSize,
                        ),
                  ).replace("[S]", selectedSize?.label || ""),
                }}
              />
            </div>
          </div>
        </div>
      )}
      {similarProducts?.length &&
      isSizeUnavailable &&
      brandDefined?.name !== "SRP" &&
      brandDefined?.name !== "Asphalte" ? (
        <UnavailableGallery similarProducts={similarProducts} />
      ) : null}
      {selectedSize && selectedSize?.variant_id && !isSizeUnavailable ? (
        <button
          tabIndex={0}
          type="button"
          className={`continue-button step-bottom-result`}
          onMouseEnter={(e) => {
            if (isMobile) return;
            setHovered(true);
            if (!isSizeUnavailable) {
              Object.assign(e.currentTarget.style, hoverStylesContinue);
            }
          }}
          onMouseLeave={(e) => {
            setHovered(false);
            if (!isSizeUnavailable) {
              Object.assign(e.currentTarget.style, CTAStyles);
            }
          }}
          disabled={isSizeUnavailable}
          onClick={() => {
            if (selectedSize?.variant_id) {
              addToCart();
              sendIds();
              postCloseIframe();
            }
          }}
          style={CTAStyles}
        >
          <div style={{ display: "inline-flex", alignItems: "center", gap: 0 }}>
            {t("button").replace("[S]", selectedSize?.label || "")}

            {isIntersport && (
              <span style={arrowContainerStyle} aria-hidden>
                <ChevronRight style={iconStyle} />
              </span>
            )}
          </div>
        </button>
      ) : null}
      <span
        role="button"
        tabIndex={0}
        className="skip"
        onClick={() => {
          const currentDomain = getQueryParam("domain");
          if (userContext && currentDomain) {
            logger.log("restart called");
            restart();
            localStorage.removeItem("uid");
            localStorage.removeItem("mid");
            postClearUid();
            postClearMid();
            userContext?.createNewUser(currentDomain);
            const urlParams = new URLSearchParams(window.location.search);
            removeLocalStore(`${urlParams.get("pid")}`);
          }
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            e.currentTarget.click();
          }
        }}
        style={{
          backgroundColor: "transparent",
          fontWeight: 400,
          fontSize: "13px",
          color: "#2E2E2E",
          textTransform: "capitalize",
          textDecoration: resultScreenUI.restartCTA
            .textDecoration as React.CSSProperties["textDecoration"],
          alignItems: "center",
          marginTop: "20px",
          letterSpacing: (resultScreenUI.restartCTA as any)?.letterSpacing,
          maxHeight: isTDF ? "28px" : isGivenchy ? "40px" : "fit-content",
          padding: isTDF
            ? "0"
            : isPeserico
              ? "12px 16px"
              : isVB
                ? "7px"
                : "16px",
          height: isTDF
            ? "28px"
            : resultScreenUI.restartCTA.borderWidth
              ? "50px"
              : "auto",
        }}
      >
        {skipTextTransform === "capitalize"
          ? capitalizeFirstLetter(t("skip"))
          : t("skip")}
      </span>
      <MeasuredBy step={step} />
    </div>
  );
};

export default ShoeResult;
